import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import CompareSlider from "./CompareSlider";

const cinzel = "font-['Cinzel',serif]";

/** Only verified fields are listed. */
function getMetaRows(result) {
  const rows = [];
  if (typeof result.grafts === "number") rows.push(["Grafts", result.grafts.toLocaleString("en-IN")]);
  if (result.technique) rows.push(["Technique", result.technique]);
  if (result.area) rows.push(["Treatment area", result.area]);
  if (result.timeline) rows.push(["Follow-up", result.timeline]);
  return rows;
}

export default function ResultModal({ result, position, total, onClose, onPrev, onNext }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // focus handling, Esc, Tab trap, scroll lock, restore focus — set up once while mounted
  useEffect(() => {
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll('button:not([disabled]), [tabindex="0"], a[href]');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, []);

  // light entrance
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      gsap.fromTo("[data-modal-backdrop]", { opacity: 0 }, { opacity: 1, duration: 0.25, ease: "power1.out" });
      gsap.fromTo(panelRef.current, { opacity: 0, y: 24, scale: 0.98 }, { opacity: 1, y: 0, scale: 1, duration: 0.35, ease: "power3.out" });
    }, panelRef);
    return () => ctx.revert();
  }, []);

  const rows = getMetaRows(result);

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-6">
      <div data-modal-backdrop onClick={onClose} className="absolute inset-0 bg-[#0e0905]/85 backdrop-blur-sm" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Before and after — ${result.label}`}
        className="relative flex max-h-[100dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[24px] border border-[#c9a84c]/30 bg-[linear-gradient(145deg,#1a0f02,#0e0905)] text-white shadow-[0_30px_90px_rgba(0,0,0,0.6)] sm:max-h-[92dvh] sm:rounded-[24px]"
      >
        {/* header */}
        <div className="flex items-center justify-between gap-3 border-b border-[#c9a84c]/20 px-5 py-3.5 sm:px-6">
          <div className="min-w-0">
            <p className={`${cinzel} text-[9px] uppercase tracking-[0.26em] text-[#c9a84c]/80`}>
              Transformation {String(position).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </p>
            <h3 className={`${cinzel} truncate text-[17px] font-bold tracking-[0.04em] text-white sm:text-[20px]`}>{result.label}</h3>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#c9a84c]/40 bg-[#c9a84c]/10 text-[#ffe9a0] transition-colors hover:bg-[#c9a84c] hover:text-[#1a0f02]"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* body */}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-y-auto overscroll-contain p-4 sm:p-6 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]" style={{ WebkitOverflowScrolling: "touch" }}>
          {/* key forces the slider back to 50% for each patient */}
          <CompareSlider
            key={result.id}
            before={result.before}
            after={result.after}
            altBefore={`Before hair transplant — ${result.label}`}
            altAfter={`After hair transplant — ${result.label}`}
            className="aspect-[4/5] max-h-[62dvh] md:max-h-[70dvh]"
          />

          <div className="flex flex-col">
            {rows.length > 0 ? (
              <dl className="divide-y divide-[#c9a84c]/15 rounded-xl border border-[#c9a84c]/20 bg-white/[0.03]">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-4 px-4 py-3">
                    <dt className={`${cinzel} text-[10px] uppercase tracking-[0.16em] text-[#c9a84c]/80`}>{k}</dt>
                    <dd className="text-right font-['Raleway',sans-serif] text-[14px] font-medium text-white">{v}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-['Raleway',sans-serif] text-[13px] text-white/60">Treatment details for this patient are not listed.</p>
            )}

            <p className="mt-4 font-['Raleway',sans-serif] text-[12px] leading-[1.7] text-white/60">
              Individual results vary. A consultation is the best way to understand what a hair restoration plan could look like for you.
            </p>

            <div className="mt-auto flex items-center gap-3 pt-6">
              <button
                type="button"
                onClick={onPrev}
                disabled={position <= 1}
                aria-label="Previous transformation"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#c9a84c]/40 bg-[#c9a84c]/10 text-[#ffe9a0] transition-all enabled:hover:bg-[#c9a84c] enabled:hover:text-[#1a0f02] disabled:opacity-35"
              >
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={position >= total}
                aria-label="Next transformation"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#c9a84c]/40 bg-[#c9a84c]/10 text-[#ffe9a0] transition-all enabled:hover:bg-[#c9a84c] enabled:hover:text-[#1a0f02] disabled:opacity-35"
              >
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}